// ══════════════════════════════════════════════════════════════
// COMPASS – JWT Decoder Utility
// ══════════════════════════════════════════════════════════════
import { AuthUser } from '../models/auth-user.model';

export interface DecodedToken {
  sub: string; // Subject (user ID)
  email?: string;
  email_verified?: boolean;
  name?: string;
  given_name?: string;
  family_name?: string;
  preferred_username?: string;
  realm_access?: {
    roles: string[];
  };
  resource_access?: {
    [key: string]: {
      roles: string[];
    };
  };
  permissions?: string[];
  exp: number; // Expiration time (seconds since epoch)
  iat: number; // Issued at (seconds since epoch)
  auth_time?: number;
  jti?: string;
  iss?: string;
  aud?: string | string[];
  typ?: string;
  azp?: string;
  session_state?: string;
  scope?: string;
  preferred_language?: string;
}

const UTF8 = new TextDecoder('utf-8');

/**
 * Decodes a JWT token and returns the payload
 * @param token - The JWT token string
 * @returns Decoded token payload or null if invalid
 */
export function decodeToken(token: string): DecodedToken | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('Invalid JWT token format');
      return null;
    }

    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    // Base64URL strips padding; restore it before atob() to avoid InvalidCharacterError.
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    // atob() gives one character per byte; the payload is UTF-8, so "é" (C3 A9) would read as "Ã©".
    const bytes = Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
    const decoded = JSON.parse(UTF8.decode(bytes));
    return decoded as DecodedToken;
  } catch (error) {
    console.error('Error decoding JWT token:', error);
    return null;
  }
}

/**
 * Extracts user information from the decoded token.
 * Roles and permissions come from the per-management-company permissions API,
 * not from the token, so both are initialized as empty arrays here.
 */
export function extractUserFromToken(token: string): AuthUser | null {
  const decoded = decodeToken(token);
  if (!decoded) {
    return null;
  }

  const firstName = decoded.given_name ?? '';
  const lastName = decoded.family_name ?? '';
  const name = decoded.name ?? `${firstName} ${lastName}`.trim();

  return {
    id: decoded.sub,
    username: decoded.preferred_username ?? decoded.email ?? decoded.sub,
    email: decoded.email ?? '',
    firstName,
    lastName,
    name,
    roles: [],
    permissions: [],
    preferredLanguage: decoded.preferred_language,
  };
}

/**
 * Checks if a token is expired
 */
export function isTokenExpired(token: string): boolean {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) {
    return true;
  }

  const now = Math.floor(Date.now() / 1000);
  return decoded.exp < now;
}
